import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Combobox,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { useInsertGoogleFontsUrl } from "@v2/features/design-library/hooks/useInsertGoogleFontsUrl";
import FontFamilyCombobox from "@v2/features/modifiers/base/FontFamilyCombobox";
import {
  LINE_HEIGHT_OPTIONS,
  TEXT_SPACING_OPTIONS,
} from "@v2/features/modifiers/constants";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { fontRouter } from "@v2/trpc/routes/font";
import {
  ALargeSmall,
  ArrowLeft,
  ChevronUp,
  LetterText,
  MoveHorizontal,
  Pencil,
} from "lucide-react";
import {
  convertFontWeightToReadableString,
  FONT_WEIGHT_OPTIONS,
  GOOGLE_FONTS_SET,
} from "replo-runtime/shared/utils/font";

import { DesignLibraryIcon } from "./designLibraryIcon";
import { defaultTypography } from "./utils/presets";

interface DesignLibraryTypographyProps {
  styleLibrary: StyleLibrary | null;
  onBack?: () => void;
  onCustomFontsClick?: () => void;
  onFontPresetsClick?: () => void;
  onStyleLibraryChange: (styleLibrary: StyleLibrary) => void;
}

export const DesignLibraryTypography: React.FC<
  DesignLibraryTypographyProps
> = ({
  styleLibrary,
  onBack,
  onCustomFontsClick,
  onFontPresetsClick,
  onStyleLibraryChange,
}) => {
  const projectId = useCurrentProjectId();

  const { data: projectFonts = [] } = fontRouter.getFonts.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const [expandedFont, setExpandedFont] = React.useState<string | null>(null);

  const fonts = React.useMemo(() => {
    if (!styleLibrary?.typography) {
      return [];
    }

    return Object.values(styleLibrary.typography).map((font) => ({
      name: font.name,
      weight: font.weight,
    }));
  }, [styleLibrary?.typography]);

  useInsertGoogleFontsUrl(fonts);

  const typography = {
    h1: styleLibrary?.typography?.h1 ?? defaultTypography.h1,
    h2: styleLibrary?.typography?.h2 ?? defaultTypography.h2,
    h3: styleLibrary?.typography?.h3 ?? defaultTypography.h3,
    h4: styleLibrary?.typography?.h4 ?? defaultTypography.h4,
    body: styleLibrary?.typography?.body ?? defaultTypography.body,
  };

  const sizeItems: ComboboxItemWithValue[] = [
    { value: "12", label: "12px" },
    { value: "14", label: "14px" },
    { value: "16", label: "16px" },
    { value: "18", label: "18px" },
    { value: "20", label: "20px" },
    { value: "24", label: "24px" },
    { value: "28", label: "28px" },
    { value: "32", label: "32px" },
    { value: "36", label: "36px" },
    { value: "48", label: "48px" },
    { value: "56", label: "56px" },
    { value: "64", label: "64px" },
  ];

  // Note (Nick, 2025-11-11): Use shared constants from modifiers to ensure consistency
  // NOTE (Kevin, 2025-12-08): Filter out null-valued "Auto" options since design library
  // typography settings require explicit values, not auto/null
  const lineHeightItems: ComboboxItemWithValue[] = LINE_HEIGHT_OPTIONS.filter(
    (option) => option.value !== null,
  ).map((option) => ({
    value: option.value!.toString(),
    label: option.label,
  }));

  const spacingItems: ComboboxItemWithValue[] = TEXT_SPACING_OPTIONS.filter(
    (option) => option.value !== null,
  ).map((option) => ({
    value: option.value!.toString(),
    label: option.label,
  }));

  const getWeightItems = (fontFamily: string): ComboboxItemWithValue[] => {
    const isGoogleFont = GOOGLE_FONTS_SET.has(fontFamily);

    if (isGoogleFont) {
      return FONT_WEIGHT_OPTIONS.map((fontWeight) => ({
        value: fontWeight.value.toString(),
        label: fontWeight.label,
      }));
    }

    const fontsForFamily = projectFonts.filter(
      (font) => font.name === fontFamily,
    );

    return fontsForFamily.map((font) => {
      const isItalic = font.style === "italic";
      const weightLabel = convertFontWeightToReadableString(
        font.weight.toString(),
      );
      const label = isItalic ? `${weightLabel} Italic` : weightLabel;
      const value = isItalic ? `${font.weight}-italic` : font.weight.toString();

      return { value, label };
    });
  };

  const getWeightValue = (settings: {
    name: string;
    weight: number;
    url: string;
  }): string => {
    const isGoogleFont = GOOGLE_FONTS_SET.has(settings.name);
    if (isGoogleFont) {
      return settings.weight.toString();
    }
    const currentFont = projectFonts.find((font) => {
      return font.name === settings.name && font.url === settings.url;
    });
    const isItalic = currentFont?.style === "italic";
    return isItalic ? `${settings.weight}-italic` : settings.weight.toString();
  };

  const headingStyles = [
    {
      name: "Heading 1",
      key: "h1",
      settings: typography.h1,
      previewText: "Aa",
    },
    {
      name: "Heading 2",
      key: "h2",
      settings: typography.h2,
      previewText: "Aa",
    },
    {
      name: "Heading 3",
      key: "h3",
      settings: typography.h3,
      previewText: "Aa",
    },
    {
      name: "Heading 4",
      key: "h4",
      settings: typography.h4,
      previewText: "Aa",
    },
  ];

  const bodyStyles = [
    {
      name: "Paragraph",
      key: "body",
      settings: typography.body,
      previewText: "Aa",
    },
  ];

  const handleChangeExpandedFont = (fontKey: string) => {
    const newExpandedFont = expandedFont === fontKey ? null : fontKey;
    setExpandedFont(newExpandedFont);
  };

  const handleTypographyChange = async (
    fontKey: string,
    property: "name" | "size" | "weight" | "lineHeight" | "letterSpacing",
    value: string,
  ) => {
    if (!styleLibrary) {
      return;
    }

    const currentFont = typography[fontKey as keyof typeof typography];
    const updatedFont = { ...currentFont };

    if (property === "name") {
      const selectedFont = projectFonts.find((font) => font.name === value);
      updatedFont.name = value;
      updatedFont.url = selectedFont?.url ?? "";

      const isGoogleFont = GOOGLE_FONTS_SET.has(value);
      if (!isGoogleFont) {
        const fontsForFamily = projectFonts.filter(
          (font) => font.name === value,
        );
        if (fontsForFamily.length > 0) {
          const availableWeights = fontsForFamily.map((font) => {
            return Number.parseInt(font.weight, 10);
          });
          if (!availableWeights.includes(updatedFont.weight)) {
            updatedFont.weight = availableWeights[0] ?? 400;
            const matchingFont = fontsForFamily.find(
              (font) => Number.parseInt(font.weight, 10) === updatedFont.weight,
            );
            updatedFont.url = matchingFont?.url ?? selectedFont?.url ?? "";
          }
        }
      }
    } else if (property === "size") {
      updatedFont.size = Number.parseInt(value, 10);
    } else if (property === "weight") {
      const isItalic = value.includes("-italic");
      const newWeight = Number.parseInt(value.replace("-italic", ""), 10);
      const style = isItalic ? "italic" : "normal";

      updatedFont.weight = newWeight;

      const isGoogleFont = GOOGLE_FONTS_SET.has(currentFont.name);
      if (!isGoogleFont) {
        const selectedFont = projectFonts.find((font) => {
          return (
            font.name === currentFont.name &&
            Number.parseInt(font.weight, 10) === newWeight &&
            font.style === style
          );
        });
        updatedFont.url = selectedFont?.url ?? currentFont.url;
      }
    } else if (property === "lineHeight") {
      updatedFont.lineHeight = Number.parseFloat(value);
    } else if (property === "letterSpacing") {
      updatedFont.letterSpacing = Number.parseFloat(value);
    }

    const updatedStyleLibrary = {
      ...styleLibrary,
      typography: {
        ...typography,
        [fontKey]: updatedFont,
      },
    };

    onStyleLibraryChange(updatedStyleLibrary);
  };

  const renderTypographyControls = (
    styleKey: string,
    settings: (typeof typography)["h1"],
  ) => {
    const weightItems = getWeightItems(settings.name);
    const weightValue = getWeightValue(settings);
    const selectedWeight =
      weightItems.find((item) => item.value === weightValue) ?? null;
    const selectedSize =
      sizeItems.find((item) => item.value === settings.size.toString()) ?? null;
    const selectedLineHeight =
      lineHeightItems.find(
        (item) => item.value === String(settings.lineHeight),
      ) ?? null;
    const selectedSpacing =
      spacingItems.find(
        (item) => item.value === String(settings.letterSpacing),
      ) ?? null;

    return (
      <div
        className="flex flex-col gap-3 w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
            <span className="text-xs text-muted">Font</span>
            <FontFamilyCombobox
              value={settings.name}
              onChange={(value) => {
                return void handleTypographyChange(styleKey, "name", value);
              }}
              size="sm"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
            <span className="text-xs text-muted">Size</span>
            <Combobox
              items={sizeItems}
              value={selectedSize}
              onValueChange={(item) => {
                if (item) {
                  void handleTypographyChange(styleKey, "size", item.value);
                }
              }}
              isItemEqualToValue={(a, b) => a.value === b.value}
              trigger={
                <ComboboxTriggerButton
                  className="w-full"
                  size="sm"
                  startEnhancer={
                    <ALargeSmall size={14} className="text-muted" />
                  }
                >
                  {`${settings.size}px`}
                </ComboboxTriggerButton>
              }
              triggerAsChild
              placeholder="Search..."
            />
          </div>
        </div>

        <div className="flex gap-3 w-full">
          <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
            <span className="text-xs text-muted">Weight</span>
            <Combobox
              items={weightItems}
              value={selectedWeight}
              onValueChange={(item) => {
                if (item) {
                  void handleTypographyChange(styleKey, "weight", item.value);
                }
              }}
              isItemEqualToValue={(a, b) => a.value === b.value}
              trigger={
                <ComboboxTriggerButton className="w-full" size="sm">
                  {selectedWeight?.label ?? weightValue}
                </ComboboxTriggerButton>
              }
              triggerAsChild
              placeholder="Search..."
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
            <span className="text-xs text-muted">Line</span>
            <Combobox
              items={lineHeightItems}
              value={selectedLineHeight}
              onValueChange={(item) => {
                if (item) {
                  void handleTypographyChange(
                    styleKey,
                    "lineHeight",
                    item.value,
                  );
                }
              }}
              isItemEqualToValue={(a, b) => a.value === b.value}
              trigger={
                <ComboboxTriggerButton
                  className="w-full"
                  size="sm"
                  startEnhancer={
                    <LetterText size={14} className="text-muted" />
                  }
                >
                  {selectedLineHeight?.label ?? String(settings.lineHeight)}
                </ComboboxTriggerButton>
              }
              triggerAsChild
              placeholder="Search..."
            />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-0 overflow-hidden">
            <span className="text-xs text-muted">Spacing</span>
            <Combobox
              items={spacingItems}
              value={selectedSpacing}
              onValueChange={(item) => {
                if (item) {
                  void handleTypographyChange(
                    styleKey,
                    "letterSpacing",
                    item.value,
                  );
                }
              }}
              isItemEqualToValue={(a, b) => a.value === b.value}
              trigger={
                <ComboboxTriggerButton
                  className="w-full"
                  size="sm"
                  startEnhancer={
                    <MoveHorizontal size={14} className="text-muted" />
                  }
                >
                  {selectedSpacing?.label ?? String(settings.letterSpacing)}
                </ComboboxTriggerButton>
              }
              triggerAsChild
              placeholder="Search..."
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-xl border-0.5 border-border bg-white shadow-sm">
      <div className="flex flex-col gap-4 self-stretch p-6 border-b-0.5 border-border">
        <div className="flex justify-between items-center self-stretch">
          <div className="flex items-center gap-3">
            <Button
              icon
              variant="tertiary"
              size="sm"
              onClick={() => {
                onBack?.();
              }}
            >
              <ArrowLeft />
            </Button>
            <div className="text-base font-semibold">Typography</div>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" size="sm" onClick={onCustomFontsClick}>
              Custom Fonts
            </Button>
            <Button
              variant="secondary"
              size="sm"
              startEnhancer={<DesignLibraryIcon />}
              onClick={onFontPresetsClick}
            >
              Presets
            </Button>
          </div>
        </div>
        <div className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg bg-sheet border-0.5 border-black/10 gap-1">
          <div
            className="flex h-8 flex-col justify-center items-center self-stretch text-center"
            style={{
              fontFamily: typography.h1?.name
                ? `'${typography.h1.name}', sans-serif`
                : undefined,
              fontSize: "42px",
              fontWeight: typography.h1?.weight ?? 600,
            }}
          >
            Heading
          </div>
          <div
            style={{
              fontFamily: typography.body?.name
                ? `'${typography.body.name}', sans-serif`
                : undefined,
              fontSize: "16px",
              fontWeight: typography.body?.weight ?? 400,
            }}
          >
            This is your paragraph.
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col self-stretch overflow-y-auto no-scrollbar">
        <div className="flex flex-col gap-2 px-6 py-4 border-b-0.5 border-border">
          <div className="text-xs font-medium text-muted">Headings</div>
          <div className="flex flex-col gap-4">
            {headingStyles.map((heading) => (
              <div
                key={heading.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedFont === heading.key
                    ? "rounded-lg border border-border bg-white shadow-sm p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  handleChangeExpandedFont(heading.key);
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-23 rounded-lg bg-sheet group-hover:bg-white flex items-center justify-center flex-shrink-0 transition-all duration-300 ease-in-out border-0.5 border-black/10",
                      expandedFont === heading.key ? "h-35" : "h-16",
                    )}
                  >
                    <span
                      style={{
                        fontFamily: heading.settings.name,
                        fontSize: `${Math.min(heading.settings.size, 32)}px`,
                        fontWeight: heading.settings.weight,
                        lineHeight: heading.settings.lineHeight ?? 1.2,
                        letterSpacing: `${heading.settings.letterSpacing}em`,
                      }}
                    >
                      {heading.previewText}
                    </span>
                  </div>
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col justify-center",
                        expandedFont === heading.key
                          ? "border-b-0.5 border-border pb-2"
                          : "min-h-16",
                      )}
                    >
                      <div className="text-sm font-medium">{heading.name}</div>
                      {expandedFont !== heading.key && (
                        <div className="text-xs text-muted">
                          {heading.settings.name} • {heading.settings.size}px •{" "}
                          {convertFontWeightToReadableString(
                            heading.settings.weight.toString(),
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedFont === heading.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div className="flex flex-col gap-4 min-h-0 w-full overflow-hidden">
                        {renderTypographyControls(
                          heading.key,
                          heading.settings,
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    icon
                    variant="secondary"
                    size="base"
                    UNSAFE_className={twMerge(
                      "transition-opacity",
                      expandedFont === heading.key
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChangeExpandedFont(heading.key);
                    }}
                  >
                    {expandedFont === heading.key ? <ChevronUp /> : <Pencil />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Body Section */}
        <div className="flex flex-col gap-2 px-6 py-4">
          <div className="text-xs font-medium text-muted">Body</div>
          <div className="flex flex-col gap-4">
            {bodyStyles.map((body) => (
              <div
                key={body.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedFont === body.key
                    ? "rounded-lg border border-border bg-white shadow-sm p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  handleChangeExpandedFont(body.key);
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-23 rounded-lg bg-sheet group-hover:bg-white flex items-center justify-center flex-shrink-0 transition-all duration-300 ease-in-out border-0.5 border-black/10",
                      expandedFont === body.key ? "h-35" : "h-16",
                    )}
                  >
                    <span
                      style={{
                        fontFamily: body.settings.name,
                        fontSize: `${Math.min(body.settings.size, 24)}px`,
                        fontWeight: body.settings.weight,
                        lineHeight: body.settings.lineHeight ?? 1.4,
                        letterSpacing: `${body.settings.letterSpacing}em`,
                      }}
                    >
                      {body.previewText}
                    </span>
                  </div>
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col justify-center",
                        expandedFont === body.key
                          ? "border-b-0.5 border-border pb-2"
                          : "min-h-16",
                      )}
                    >
                      <div className="text-sm font-medium">{body.name}</div>
                      {expandedFont !== body.key && (
                        <div className="text-xs text-muted">
                          {body.settings.name} • {body.settings.size}px •{" "}
                          {convertFontWeightToReadableString(
                            body.settings.weight.toString(),
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedFont === body.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div className="flex flex-col gap-4 min-h-0 w-full overflow-hidden">
                        {renderTypographyControls(body.key, body.settings)}
                      </div>
                    </div>
                  </div>
                  <Button
                    icon
                    variant="secondary"
                    size="base"
                    UNSAFE_className={twMerge(
                      "transition-opacity",
                      expandedFont === body.key
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChangeExpandedFont(body.key);
                    }}
                  >
                    {expandedFont === body.key ? <ChevronUp /> : <Pencil />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
